import { Exception, LaunchDarklyService, ToasterService, Validator } from '@anddone/ui-utilities';
import { Component, Renderer2, signal, OnInit, OnDestroy, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ValidationConstant } from '../../../constants/validation.constant';
import { MessageConstants } from '../../../constants/message.constant';
import { LoginService } from '../../../services/login-service';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { RecaptchaV3Module, ReCaptchaV3Service } from 'ng-recaptcha';
import { AppSetting } from '../../../constants/appsetting.constant';

@Component({
  selector: 'app-forgot-password',
  imports: [CommonModule, TranslateModule, ReactiveFormsModule, RecaptchaV3Module],
  templateUrl: './forgot-password.html',
  styleUrl: './forgot-password.scss',
})
export class ForgotPassword implements OnInit, OnDestroy {
  forgotPasswordForm!: FormGroup;
  forgotPasswordFormErrors: any = {};
  isLoader = signal<boolean>(false);
  validator: Validator;
  subscription!: Subscription;
  featureFlags: any;
  currentYear: number = new Date().getFullYear();
  appSetting = AppSetting;

  config = {
    userName: {
      required: { name: ValidationConstant.forgotPassword.username.name },
      maxlength: {
        name: ValidationConstant.forgotPassword.username.name,
        max: ValidationConstant.forgotPassword.username.maxLength.toString(),
      },
      minlength: {
        name: ValidationConstant.forgotPassword.username.name,
        min: ValidationConstant.forgotPassword.username.minLength.toString(),
      },
    },
  };

  private formBuilder = inject(FormBuilder);
  private loginService = inject(LoginService);
  private router = inject(Router);
  private toasterService = inject(ToasterService);
  private reCaptchaV3Service = inject(ReCaptchaV3Service);
  private renderer = inject(Renderer2);
  private launchDarklyService = inject(LaunchDarklyService);
  constructor() {
    this.validator = new Validator(this.config);
  }

  ngOnInit() {
    this.forgotPasswordForm = this.formBuilder.group({
      userName: ['', [Validators.required]],
    });
    this.forgotPasswordForm.valueChanges.subscribe((data) => this.onValueChanged(data));
    this.renderer.addClass(document.body, 'recaptcha');

    // Get all feature flags for the project and subscribe to changes
    this.featureFlags = this.launchDarklyService.getAllFeatureFlags(
      AppSetting.launchDarklyProjectName
    );
    this.launchDarklyService.LD_emitters[AppSetting.launchDarklyProjectName]?.subscribe(
      (allFlags: any) => {
        this.featureFlags = allFlags;
      }
    );
  }

  // form on change value
  onValueChanged(_data?: any) {
    if (!this.forgotPasswordForm) {
      return;
    }
    this.forgotPasswordFormErrors = this.validator.validate(this.forgotPasswordForm);
  }

  // get recaptcha token
  executeRecaptcha() {
    this.subscription = this.reCaptchaV3Service
      ?.execute('forgotPassword')
      ?.subscribe((token: any) => this.forgotPassword(token));
  }

  // submit forget password api call
  forgotPassword(token: any) {
    this.validator.validateAllFormFields(this.forgotPasswordForm);
    this.forgotPasswordFormErrors = this.validator.validate(this.forgotPasswordForm);
    if (this.forgotPasswordForm?.invalid) {
      return;
    }

    this.isLoader.set(true);
    const userName = this.forgotPasswordForm?.controls['userName'].value;
    const reqObj = {
      CaptchaToken: token,
      ActionName: 'forgotPassword',
    };

    if (this.featureFlags?.suppress_recaptcha_validations_reset_password) {
      reqObj.CaptchaToken = null;
    }

    this.loginService.forgotPassword(userName, reqObj)?.subscribe({
      next: () => {
        this.isLoader.set(false);
        this.toasterService.showSuccess(MessageConstants.forgotPassword.passwordSent, 'full');
        this.router.navigate(['/login']);
      },
      error: (err: any) => {
        this.isLoader.set(false);
        const toastMessage = Exception.exceptionMessage(err);
        this.toasterService.showError(toastMessage.join(', '), 'full');
      },
    });
  }

  back() {
    this.router.navigate(['/login']);
  }

  goToLink(url: string) {
    window.open(url, '_blank');
  }

  ngOnDestroy(): void {
    this.renderer.removeClass(document.body, 'recaptcha');
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
